import React, { useCallback, useMemo } from 'react';

import {
  VersionView,
  DocumentContainer,
  ComplexArtboardWrapper,
} from '../ComplexDocumentDesign/styles';

import ComplexArtboardProvider, {
  CONTEXT_KEYS,
  useArtboardContext,
} from '../../context/complexArtboardContext';
import ComplexDocumentHistoryProvider, { COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import BusinessLogicProvider from '../../context/businessLogicContext';
import ComplexEmailProvider from '../../context/complexEmailContext';
import FontsProvider from '../../context/fontsContext';
import ComplexEmailArtboard from './ComplexEmailArtboard';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../context/complexVersionContext';
import ComplexEmailSidebar from './ComplexEmailSidebar';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';

const OVERRIDE_ARTBOARD_STATE = {
  [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
  [CONTEXT_KEYS.ENABLE_RULER]: false,
};

const OVERRIDE_DOCUMENT_CONTEXT_STATE = { [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]: true };

const ComplexArtboardPreviewContainer = ({
  currentVisibleVersion,
  documentId,
  existingDocument,
}) => {
  const previewSelector = useCallback(store => store[CONTEXT_KEYS.IS_PREVIEW], []);
  const [isPreview] = useArtboardContext(previewSelector);

  const accessibilitySelector = useCallback(store => store[CONTEXT_KEYS.CHECK_ACCESSIBILITY], []);
  const [accessibilityView] = useArtboardContext(accessibilitySelector);

  return (
    <div style={{ display: `${isPreview || accessibilityView ? 'none' : 'contents'}` }}>
      <ComplexEmailArtboard
        templateId={documentId}
        version={currentVisibleVersion}
        readOnly={true}
        existingDocument={existingDocument}
      />
    </div>
  );
};

const ComplexEmailVersion = ({ documentId }) => {
  const draftEditableSelector = useCallback(
    store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN],
    [],
  );
  const [editable] = useArtboardContext(draftEditableSelector);

  const lifecycleSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE],
    [],
  );
  const [lifeCycle] = useVersionContext(lifecycleSelector, `ComplexEmailVersion`);

  const currentVisibleVersionSelector = useCallback(
    store => ({
      currentVisibleVersion: store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION],
      currentVisibleTag: store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG],
    }),
    [],
  );
  const [{ currentVisibleVersion, currentVisibleTag }] = useVersionContext(
    currentVisibleVersionSelector,
  );

  const tagDocument = currentVisibleTag?.tagDocument;

  const previewPortalContainerId = `preview-portal-${
    currentVisibleTag?.tagId || currentVisibleVersion === 'draft'
      ? 'qaready'
      : currentVisibleVersion
  }`;

  const customArtboardState = useMemo(() => {
    return {
      ...OVERRIDE_ARTBOARD_STATE,
      [CONTEXT_KEYS.IS_DRAFT_EDITABLE]: editable,
    };
  }, [editable]);

  return (
    currentVisibleVersion !== COMPLEX_DOCUMENT_VERSION.DRAFT && (
      <VersionView>
        {lifeCycle[currentVisibleVersion] || tagDocument ? (
          <ComplexDocumentHistoryProvider customState={OVERRIDE_DOCUMENT_CONTEXT_STATE}>
            <ComplexArtboardProvider
              documentId={documentId}
              customState={customArtboardState}
              customName={'VersionContext'}
            >
              <ComplexEmailProvider>
                <BusinessLogicProvider>
                  <FontsProvider>
                    <ComplexEmailSidebar />
                    <ComplexArtboardWrapper className="readOnly versions">
                      <DocumentContainer className={'environment email-environment version'}>
                        <div id={previewPortalContainerId}></div>
                        <ComplexArtboardPreviewContainer
                          documentId={tagDocument ? undefined : documentId}
                          currentVisibleVersion={currentVisibleVersion}
                          existingDocument={tagDocument}
                        />
                      </DocumentContainer>
                    </ComplexArtboardWrapper>
                  </FontsProvider>
                </BusinessLogicProvider>
              </ComplexEmailProvider>
            </ComplexArtboardProvider>
          </ComplexDocumentHistoryProvider>
        ) : null}
      </VersionView>
    )
  );
};

export default ComplexEmailVersion;
